import type React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface Tab {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

const TrophyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

const CompassIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" />
  </svg>
);

const UserIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="8" r="5" />
    <path d="M20 21a8 8 0 0 0-16 0" />
  </svg>
);

const tabs: Tab[] = [
  { id: 'leagues', label: 'Leagues', icon: <TrophyIcon />, path: '/leagues' },
  { id: 'info', label: 'Guide', icon: <CompassIcon />, path: '/how-it-works' },
  { id: 'profile', label: 'Profile', icon: <UserIcon />, path: '/profile' },
];

interface BottomTabBarProps {
  onInfoClick?: () => void;
  isInfoActive?: boolean;
}

export function BottomTabBar({ onInfoClick, isInfoActive }: BottomTabBarProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (tab: Tab) => {
    if (tab.id === 'info' && isInfoActive) return true;

    const path = tab.path;
    if (path === '/leagues') {
      return (
        location.pathname === '/leagues' ||
        location.pathname === '/home' ||
        location.pathname === '/dashboard'
      );
    }
    if (path === '/how-it-works') {
      return ['/how-it-works', '/about', '/terms', '/privacy'].includes(location.pathname);
    }
    return location.pathname === path;
  };

  const handleTabClick = (tab: Tab) => {
    if (tab.id === 'info' && onInfoClick) {
      onInfoClick();
    } else if (isInfoActive && onInfoClick) {
      onInfoClick();
    } else {
      navigate(tab.path);
    }
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-[#2c2c2c] border-t border-gray-800 safe-area-inset-bottom z-50">
      <div className="flex justify-around items-center h-16">
        {tabs.map((tab) => {
          const active = isActive(tab);
          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab)}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-all relative ${
                active ? 'text-white' : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              {active && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-b-full" />
              )}
              <span className="mb-1">{tab.icon}</span>
              <span className={`text-xs font-medium ${active ? 'font-semibold' : ''}`}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
